import { useCallback, useEffect } from "react";

import { useField, useFormikContext } from "formik";

type SubmitFirstField<T, E = string> = {
  error: E | undefined;
  hasSubmitted: boolean;
  value: T;
  handleBlur: (e?: React.FocusEvent<HTMLElement>) => Promise<void>;
  handleChange: (e: React.ChangeEvent<unknown>) => void;
  setValue: (value: T) => void;
};

const useSubmitFirstField = <T, E = string>(
  name: string,
): SubmitFirstField<T, E> => {
  const [field, meta] = useField<T>(name);
  const {
    handleBlur: formikBlur,
    setFieldTouched,
    setFieldValue,
    submitCount,
    validateField,
  } = useFormikContext();

  const hasSubmitted = submitCount > 0;

  useEffect(() => {
    if (hasSubmitted) validateField(name);
  }, [field.value, hasSubmitted, name, validateField]);

  const handleBlur = useCallback(
    async (e?: React.FocusEvent<HTMLElement>): Promise<void> => {
      if (e) {
        formikBlur(e);
      } else {
        // shouldValidate: false — dropdown selection fires setValue and this blur in the
        // same tick; a validating setTouched would re-run validation with the pre-setValue
        // values snapshot and its stale result would overwrite the correct one.
        setFieldTouched(name, true, false);
      }
      if (hasSubmitted) await validateField(name);
    },
    [formikBlur, hasSubmitted, name, setFieldTouched, validateField],
  );

  const setValue = useCallback(
    (value: T): void => {
      setFieldValue(name, value);
    },
    [name, setFieldValue],
  );

  return {
    error: hasSubmitted ? (meta.error as E | undefined) : undefined,
    handleBlur,
    handleChange: field.onChange,
    hasSubmitted,
    setValue,
    value: field.value,
  };
};

export { useSubmitFirstField, type SubmitFirstField };
